/**
 * Animates the open/close transition of the <details>-based product
 * accordion items (Product Details, Key Features, Fabric Composition, Care
 * Instructions), since native <details> toggles instantly with no easing.
 */
class ProductAccordionAnimator {
  constructor(details) {
    this.details = details;
    this.summary = details.querySelector(':scope > summary');
    this.content = details.querySelector(':scope > .product-accordion__content');
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;

    if (!this.summary || !this.content) return;

    this.summary.addEventListener('click', (event) => this.onClick(event));
  }

  onClick(event) {
    event.preventDefault();
    this.details.style.overflow = 'hidden';

    if (this.isClosing || !this.details.open) {
      this.open();
    } else if (this.isExpanding || this.details.open) {
      this.shrink();
    }
  }

  shrink() {
    this.isClosing = true;

    const startHeight = `${this.details.offsetHeight}px`;

    // Measure the true closed height (including the <details> element's own
    // padding/border) by briefly toggling it closed, rather than assuming
    // it equals the summary's own box - that assumption was the cause of a
    // visible "cut off, then snap to the right size" glitch at the end of
    // the closing animation.
    this.details.open = false;
    const endHeight = `${this.details.offsetHeight}px`;
    this.details.open = true;

    if (this.animation) this.animation.cancel();

    this.animation = this.details.animate(
      { height: [startHeight, endHeight] },
      { duration: 250, easing: 'ease-out' }
    );

    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => {
      this.isClosing = false;
    };
  }

  open() {
    this.details.style.height = `${this.details.offsetHeight}px`;
    this.details.open = true;
    window.requestAnimationFrame(() => this.expand());
  }

  expand() {
    this.isExpanding = true;

    const startHeight = `${this.details.offsetHeight}px`;

    // Same fix as shrink(): measure the true open height by briefly
    // releasing the inline height constraint, instead of approximating it
    // from summary + content alone (which ignored the <details> element's
    // own padding/border and caused an end-of-animation snap).
    this.details.style.height = '';
    const endHeight = `${this.details.offsetHeight}px`;
    this.details.style.height = startHeight;

    if (this.animation) this.animation.cancel();

    this.animation = this.details.animate(
      { height: [startHeight, endHeight] },
      { duration: 250, easing: 'ease-out' }
    );

    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => {
      this.isExpanding = false;
    };
  }

  onAnimationFinish(open) {
    this.details.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.details.style.height = '';
    this.details.style.overflow = '';
  }
}

document.querySelectorAll('.product-accordion').forEach((details) => {
  if (details.dataset.animatorInitialized) return;
  details.dataset.animatorInitialized = 'true';
  new ProductAccordionAnimator(details);
});
